/* Laura Elizabeth — Charm Builder
 * Build: 2026-07-14 (v2 — fixed bubbles, per the heylove reference)
 *
 * Model (matches the reference): the chain image carries N fixed circular
 * bubbles. Drag a charm into a bubble, or tap to drop it in the next free one.
 * Dropping onto a filled bubble replaces what was there.
 *
 * Where we go beyond the reference:
 *   1. The bubbles are placed on the chain's ACTUAL hanging line, traced from
 *      the base image's alpha channel — so they follow the curve instead of
 *      being hand-typed percentages that only fit one photo.
 *   2. Each charm is measured from its own alpha channel, so a (Small-Tiny)
 *      charm really does look daintier than a (Large) one, and it hangs from
 *      its jump ring rather than floating in the middle of a circle.
 *   3. Pointer events, so drag works on touch. The reference uses HTML5
 *      drag-and-drop, which simply does not exist on a phone.
 */
(function () {
  'use strict';

  var ALPHA = 24;
  var PROBE = 256;
  var COMPOSE = 1000;

  var cache = new Map();

  function loadImage(src) {
    return new Promise(function (res, rej) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = function () { res(im); };
      im.onerror = function () { rej(new Error('img ' + src)); };
      im.src = src;
    });
  }

  // Idempotent: applying a size twice gives foo_200x_200x.png, a 404 that
  // silently poisons every measurement downstream.
  function sized(url, w) {
    if (!url) return url;
    return url
      .replace(/_(\d+)x(\d*)(\.(?:png|jpe?g|webp))/i, '$3')
      .replace(/(\.(?:png|jpe?g|webp))(\?|$)/i, '_' + w + 'x$1$2');
  }

  function alphaCtx(img, w) {
    var h = Math.max(1, Math.round(w * (img.naturalHeight / img.naturalWidth)));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, 0, 0, w, h);
    var data = x.getImageData(0, 0, w, h).data;   // may throw SecurityError on a tainted canvas
    return { data: data, w: w, h: h, blank: isBlankAlpha(data) };
  }

  // Firefox with resistFingerprinting / ETP-strict does NOT throw on getImageData;
  // it silently returns a uniform (all-white, fully-opaque) buffer. That reads as
  // "every pixel opaque" and would flat-line the chain trace and round-mask every
  // charm. Detect that uniform buffer and treat it as a failed read instead.
  function isBlankAlpha(data) {
    var n = data.length, opaque = 0, samples = 0;
    for (var i = 3; i < n; i += 4 * 97) {        // sparse stride, plenty for a yes/no
      samples++;
      if (data[i] > ALPHA) opaque++;
    }
    return samples > 0 && opaque === samples;    // no transparency anywhere = blocked/blank
  }

  /* ── measure a charm's silhouette inside its own frame ── */
  function measureCharm(src) {
    if (cache.has(src)) return Promise.resolve(cache.get(src));
    var p = loadImage(sized(src, 200)).then(function (img) {
      var g = alphaCtx(img, PROBE);
      // Canvas readback blocked (RFP/ETP): we can't measure a silhouette. Use a
      // centred generic box with a top-edge ring contact so the charm still hangs
      // sensibly, rather than getting round-masked as a "photo".
      if (g.blank) {
        var b = { l: 0.18, t: 0.06, r: 0.82, b: 0.94, ar: img.naturalHeight / img.naturalWidth, opaque: false, blocked: true };
        b.ring = { x: 0.5, y: 0.10, contactY: 0.04, bails: [{ x: 0.5, contactY: 0.04 }], found: false };
        cache.set(src, b);
        return b;
      }
      var minX = g.w, minY = g.h, maxX = -1, maxY = -1, clear = 0;
      for (var y = 0; y < g.h; y++) {
        for (var x = 0; x < g.w; x++) {
          var a = g.data[(y * g.w + x) * 4 + 3];
          if (a > ALPHA) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          } else clear++;
        }
      }
      // A real cutout is mostly empty space; a lifestyle photo is ~0% clear.
      // Nine charms here ship with no cutout at all — we mask those to a round
      // pendant rather than hang a square photo off the chain.
      var opaque = (clear / (g.w * g.h)) < 0.10;
      var box = (maxX < 0)
        ? { l: 0, t: 0, r: 1, b: 1, ar: 1, opaque: true }
        : {
            l: minX / g.w, t: minY / g.h,
            r: (maxX + 1) / g.w, b: (maxY + 1) / g.h,
            ar: img.naturalHeight / img.naturalWidth,
            opaque: opaque
          };
      if (opaque) { box.l = 0.08; box.t = 0.08; box.r = 0.92; box.b = 0.92; }

      box.ring = findRing(g, box);        // where the chain actually threads through
      cache.set(src, box);
      return box;
    }).catch(function (e) {
      console.warn('[lecb] charm measure failed:', src, e);
      var box = { l: 0.2, t: 0.2, r: 0.8, b: 0.8, ar: 1, opaque: false, failed: true };
      cache.set(src, box);
      return box;
    });
    cache.set(src, p);
    return p;
  }

  /* ── find the jump ring: the hole the chain threads through ──
     A charm's ring is a closed loop of metal, so the gap inside it is
     transparent pixels that are NOT reachable from the edge of the frame.
     Flood-fill the background from the border; whatever transparency survives
     is enclosed — a hole. The topmost hole is the jump ring, and that is the
     point that must sit on the chain. Anchoring to the top of the silhouette
     instead (what we did before) puts the ring beside the chain on any charm
     whose loop isn't dead-centre — the moon, the fish, the boot. */
  function findRing(g, box) {
    var w = g.w, h = g.h, d = g.data;
    var outside = new Uint8Array(w * h);
    var stack = [];

    function seed(x, y) {
      var i = y * w + x;
      if (!outside[i] && d[i * 4 + 3] <= ALPHA) { outside[i] = 1; stack.push(i); }
    }
    for (var x = 0; x < w; x++) { seed(x, 0); seed(x, h - 1); }
    for (var y = 0; y < h; y++) { seed(0, y); seed(w - 1, y); }

    while (stack.length) {
      var i = stack.pop(), px = i % w, py = (i / w) | 0;
      if (px > 0) seed(px - 1, py);
      if (px < w - 1) seed(px + 1, py);
      if (py > 0) seed(px, py - 1);
      if (py < h - 1) seed(px, py + 1);
    }

    var top = box.t * h;
    var height = (box.b - box.t) * h;
    var bboxArea = (box.r - box.l) * w * height;

    // A jump ring lives in the TOP FIFTH of a charm. Anything below that is
    // decoration. And it is a real blob of pixels — the evil-eye rectangles
    // contain single anti-aliased specks of transparency a third of the way
    // down, and taking the "topmost hole" latched onto one of those and hung
    // the charm from its own middle.
    var BAND = 0.22;
    var MIN = bboxArea * 0.0012;      // below this it's anti-aliasing, not a hole
    var MAX = bboxArea * 0.16;        // above this it's the body (open heart, horseshoe)

    var limitY = Math.min(h - 1, Math.round(top + height * BAND));
    var seen = new Uint8Array(w * h);
    var holes = [];

    for (var yy = Math.round(top); yy <= limitY; yy++) {
      for (var xx = Math.round(box.l * w); xx <= Math.round(box.r * w) && xx < w; xx++) {
        var k = yy * w + xx;
        if (seen[k] || outside[k] || d[k * 4 + 3] > ALPHA) continue;

        var q = [k]; seen[k] = 1;
        var sx = 0, sy = 0, n = 0, minYh = h;
        while (q.length) {
          var j = q.pop(), jx = j % w, jy = (j / w) | 0;
          sx += jx; sy += jy; n++;
          if (jy < minYh) minYh = jy;                       // top edge of this hole = its chain contact
          var nb = [jx > 0 ? j - 1 : -1, jx < w - 1 ? j + 1 : -1, jy > 0 ? j - w : -1, jy < h - 1 ? j + w : -1];
          for (var m = 0; m < 4; m++) {
            var t2 = nb[m];
            if (t2 >= 0 && !seen[t2] && !outside[t2] && d[t2 * 4 + 3] <= ALPHA) { seen[t2] = 1; q.push(t2); }
          }
        }
        if (n < MIN || n > MAX) continue;
        if ((sy / n - top) / height > BAND) continue;      // its centre must be up top too
        holes.push({ x: sx / n, y: sy / n, top: minYh, n: n });
      }
    }

    if (!holes.length) return ringFallback(g, box);

    // Several charms hang from TWO bails (the evil-eye rectangles). Keep EACH
    // bail's own x + contact edge, so seating can hang the charm so neither bail
    // floats above its own local point on the chain (see charmGeom two-bail case).
    var tot = 0, cx = 0, cy = 0, contactY = 1;
    var bails = [];
    for (var p = 0; p < holes.length; p++) {
      tot += holes[p].n;
      cx += holes[p].x * holes[p].n;
      cy += holes[p].y * holes[p].n;
      var cty = holes[p].top / h;
      if (cty < contactY) contactY = cty;
      bails.push({ x: holes[p].x / w, contactY: cty });
    }
    bails.sort(function (a, b2) { return a.x - b2.x; });
    return { x: (cx / tot) / w, y: (cy / tot) / h, contactY: contactY, bails: bails, found: true };
  }

  // No closed ring in the artwork (open loops, photos): hang it from the
  // middle of whatever metal sits at the very top of the silhouette.
  function ringFallback(g, box) {
    var w = g.w, h = g.h, d = g.data;
    var y0 = Math.round(box.t * h);
    var y1 = Math.min(h - 1, Math.round((box.t + (box.b - box.t) * 0.08) * h));
    var sx = 0, n = 0;
    for (var y = y0; y <= y1; y++) {
      for (var x = 0; x < w; x++) {
        if (d[(y * w + x) * 4 + 3] > ALPHA) { sx += x; n++; }
      }
    }
    var cx = n ? (sx / n) / w : (box.l + box.r) / 2;
    return { x: cx, y: box.t + (box.b - box.t) * 0.045, contactY: box.t, bails: [{ x: cx, contactY: box.t }], found: false };
  }

  /* ── trace the chain's hanging line ── */
  function traceChain(src) {
    return loadImage(sized(src, 400)).then(function (img) {
      var g = alphaCtx(img, PROBE);
      if (g.blank) throw new Error('chain trace: canvas readback blocked');
      var prof = new Float32Array(g.w).fill(NaN);
      var deepest = -1, deepX = g.w / 2;
      for (var x = 0; x < g.w; x++) {
        for (var y = g.h - 1; y >= 0; y--) {
          if (g.data[(y * g.w + x) * 4 + 3] > ALPHA) {
            prof[x] = y / g.h;
            if (y > deepest) { deepest = y; deepX = x; }
            break;
          }
        }
      }
      var band = (deepest / g.h) - 0.62;   // hangable span — how far UP the chain a charm may sit
      var lo = deepX, hi = deepX;
      while (lo > 0 && prof[lo - 1] >= band) lo--;
      while (hi < g.w - 1 && prof[hi + 1] >= band) hi++;

      var sm = new Float32Array(g.w);
      for (var i = 0; i < g.w; i++) {
        var win = [];
        for (var k = i - 5; k <= i + 5; k++) if (k >= 0 && k < g.w && !isNaN(prof[k])) win.push(prof[k]);
        win.sort();
        sm[i] = win.length ? win[win.length >> 1] : NaN;
      }
      // Reject an implausible trace (no depth found, or a near-zero hangable
      // span) — a flat/garbage profile would scatter the bubbles. init() falls
      // back to stored slot coords when this throws.
      if (deepest < 0 || (hi - lo) / g.w < 0.12) throw new Error('chain trace: implausible profile');

      return { prof: sm, n: g.w, arc: [lo / g.w, hi / g.w], deep: deepX / g.w,
               aspect: img.naturalHeight / img.naturalWidth, valid: true };
    });
  }

  // Synthetic fallback chain — a shallow smooth U derived only from the base
  // image aspect. Used when live tracing is blocked and no stored coords exist.
  // NOTE: Shopify's aspect_ratio (cfg.baseAspect) is W/H; chain.aspect is H/W,
  // so invert it here.
  function fallbackChain(cfg) {
    var N = PROBE, prof = new Float32Array(N);
    var aspect = cfg.baseAspect ? (1 / cfg.baseAspect) : 1;    // H/W
    var lo = 0.16, hi = 0.84, mid = 0.5, depth = 0.74, rise = 0.18;
    for (var i = 0; i < N; i++) {
      var xf = i / (N - 1);
      var t = (xf - mid) / ((hi - lo) / 2);                    // -1..1 across the arc
      prof[i] = depth - rise * Math.min(1, t * t);             // parabola: deepest at centre
    }
    return { prof: prof, n: N, arc: [lo, hi], deep: mid, aspect: aspect, valid: false, synthetic: true };
  }

  function chainY(chain, xFrac) {
    var i = Math.max(0, Math.min(chain.n - 1, Math.round(xFrac * (chain.n - 1))));
    var y = chain.prof[i];
    if (!isNaN(y)) return y;
    for (var d = 1; d < chain.n; d++) {
      if (i - d >= 0 && !isNaN(chain.prof[i - d])) return chain.prof[i - d];
      if (i + d < chain.n && !isNaN(chain.prof[i + d])) return chain.prof[i + d];
    }
    return 0.8;
  }

  /* ── the bubbles: N fixed slots sitting ON the chain ──
     The reference hard-codes each circle's percentage, which only ever fits one
     photo. We derive them from the traced chain, so the same section works for
     the saree, the beaded necklace, and anything the client adds later. */
  function bubbles(chain, cfg) {
    var n = Math.max(1, cfg.maxCharms);
    var lo = chain.arc[0], hi = chain.arc[1];

    // Sample the hangable arc finely and build a cumulative ARC-LENGTH table.
    // Spacing evenly in X (what the reference does) bunches the middle charms
    // where a U-chain is flat and stretches the outer ones up the sides — that
    // is the "uneven / clustered" look. Spacing by arc length gives equal gaps
    // ALONG the chain regardless of its curve. Units: x in width-fractions, y
    // (fraction of height) scaled by aspect, so hypot() is a true screen span.
    var SAMP = 240, xs = [], ys = [];
    for (var s = 0; s <= SAMP; s++) {
      var xf = lo + (hi - lo) * (s / SAMP);
      var yf = chainY(chain, xf);
      if (isFinite(yf)) { xs.push(xf); ys.push(yf); }
    }
    if (xs.length < 2) return evenXBubbles(chain, cfg, n);

    var arc = [0];
    for (var k = 1; k < xs.length; k++) {
      arc[k] = arc[k - 1] + Math.hypot(xs[k] - xs[k - 1], (ys[k] - ys[k - 1]) * chain.aspect);
    }
    var totalArc = arc[arc.length - 1];
    if (!(totalArc > 0)) return evenXBubbles(chain, cfg, n);

    // Centre the charms on the deepest-plateau MIDPOINT (a flat-bottomed chain
    // has many equally-deep pixels; take the middle of that run, not the first).
    var maxY = -Infinity, a;
    for (a = 0; a < ys.length; a++) if (ys[a] > maxY) maxY = ys[a];
    var loP = -1, hiP = -1;
    for (a = 0; a < ys.length; a++) if (ys[a] >= maxY - 0.01) { if (loP < 0) loP = a; hiP = a; }
    var arcMid = (arc[loP] + arc[hiP]) / 2;

    var span = Math.min(totalArc, totalArc * cfg.spread);
    var start = Math.max(0, Math.min(totalArc - span, arcMid - span / 2));
    var cell = span / n;
    var d = Math.min(cfg.bubbleSize, cell * 1.55);

    function at(target) {                       // arc position → {x,y} by linear interp
      target = Math.max(0, Math.min(totalArc, target));
      var i = 1;
      while (i < arc.length && arc[i] < target) i++;
      var i0 = i - 1, i1 = Math.min(i, arc.length - 1);
      var seg = arc[i1] - arc[i0];
      var f = seg > 0 ? (target - arc[i0]) / seg : 0;
      return { x: xs[i0] + (xs[i1] - xs[i0]) * f, y: ys[i0] + (ys[i1] - ys[i0]) * f };
    }

    var out = [];
    for (var i = 0; i < n; i++) {
      var t = (n === 1) ? arcMid : (start + (i + 0.5) * cell);
      var pt = at(t);
      var top = pt.y * chain.aspect;            // chain hang line, in width-fraction units
      out.push({ cx: pt.x, top: top, r: d / 2, cy: top + d * 0.46, chainYf: pt.y });
    }
    return out;
  }

  // Degenerate/blocked trace: fall back to the old even-X placement so bubbles
  // still land on the chain (no NaN, no scatter).
  function evenXBubbles(chain, cfg, n) {
    var arcW = chain.arc[1] - chain.arc[0];
    var span = arcW * cfg.spread, spacing = span / n;
    var d = Math.min(cfg.bubbleSize, spacing * 1.55);
    var out = [];
    for (var i = 0; i < n; i++) {
      var cx = chain.deep - span / 2 + spacing * (i + 0.5);
      var yf = chainY(chain, cx);
      var top = yf * chain.aspect;
      out.push({ cx: cx, top: top, r: d / 2, cy: top + d * 0.46, chainYf: yf });
    }
    return out;
  }

  /* ── CLUSTERED layout: all N bubbles bunched at the chain's lowest point ──
     The client's ask: charms slide on an open chain, so gravity drops them ALL
     to the bottom centre where they hang together (see the "clustered" ref).
     So instead of spreading across cfg.spread, we keep every bubble at FULL
     size (spread-mode shrinks them to fit the cell — wrong here) and pack their
     centres tightly around the deepest-plateau midpoint. PACK<1 overlaps them so
     the hung charms read as one cluster, exactly like real charms resting on the
     bottom ring. Uses the same arc-length machinery as bubbles() so the cluster
     still sits ON the traced chain whatever its curve. */
  function clusterBubbles(chain, cfg) {
    var n = Math.max(1, cfg.maxCharms);
    var lo = chain.arc[0], hi = chain.arc[1];
    var SAMP = 240, xs = [], ys = [];
    for (var s = 0; s <= SAMP; s++) {
      var xf = lo + (hi - lo) * (s / SAMP);
      var yf = chainY(chain, xf);
      if (isFinite(yf)) { xs.push(xf); ys.push(yf); }
    }
    if (xs.length < 2) return bubbles(chain, cfg);   // weak trace → fall back to spread
    var arc = [0];
    for (var k = 1; k < xs.length; k++) {
      arc[k] = arc[k - 1] + Math.hypot(xs[k] - xs[k - 1], (ys[k] - ys[k - 1]) * chain.aspect);
    }
    var totalArc = arc[arc.length - 1];
    if (!(totalArc > 0)) return bubbles(chain, cfg);

    var maxY = -Infinity, a;
    for (a = 0; a < ys.length; a++) if (ys[a] > maxY) maxY = ys[a];
    var loP = -1, hiP = -1;
    for (a = 0; a < ys.length; a++) if (ys[a] >= maxY - 0.01) { if (loP < 0) loP = a; hiP = a; }
    var arcMid = (arc[loP] + arc[hiP]) / 2;

    function at(target) {
      target = Math.max(0, Math.min(totalArc, target));
      var i = 1;
      while (i < arc.length && arc[i] < target) i++;
      var i0 = i - 1, i1 = Math.min(i, arc.length - 1);
      var seg = arc[i1] - arc[i0];
      var f = seg > 0 ? (target - arc[i0]) / seg : 0;
      return { x: xs[i0] + (xs[i1] - xs[i0]) * f, y: ys[i0] + (ys[i1] - ys[i0]) * f };
    }

    var d = cfg.bubbleSize;                         // full bubble size — no shrink
    var PACK = (cfg.clusterPack > 0 ? cfg.clusterPack : 0.72);   // centre-to-centre = PACK*d
    var step = d * PACK;
    var clusterArc = step * (n - 1);                // first-centre to last-centre
    if (clusterArc > totalArc) { step = (totalArc / Math.max(1, n - 1)) * 0.9; clusterArc = step * (n - 1); }
    var start = Math.max(0, Math.min(totalArc - clusterArc, arcMid - clusterArc / 2));

    var out = [];
    for (var i = 0; i < n; i++) {
      var t = (n === 1) ? arcMid : (start + i * step);
      var pt = at(t);
      var top = pt.y * chain.aspect;
      out.push({ cx: pt.x, top: top, r: d / 2, cy: top + d * 0.46, chainYf: pt.y });
    }
    return out;
  }

  /* ── MANUAL slots: fixed bubble positions from the theme, one per {x,y} ──
     For products whose charms hang from FIXED physical points, not a sliding
     chain — e.g. the safety-pin brooch's three jump rings. The auto-trace finds
     the pin's bottom bar, which is above (and spaced differently from) the
     rings, so it can never line the bubbles up; and after the client re-shot the
     pin larger, the trace drifted further off. Coords are fractions of the base
     image (x of width, y of height). The bubble sits ON the point (cy = top) and
     the charm hangs from it. When manual slots are active the chain is rebuilt
     WITHOUT a profile (see init) so charmGeom seats every charm on slot.chainYf
     — its own ring — instead of the traced bar. */
  function manualBubbles(chain, cfg) {
    var d = cfg.bubbleSize;
    var aspect = (chain && chain.aspect) ? chain.aspect : (cfg.baseAspect ? (1 / cfg.baseAspect) : 1);
    var n = Math.max(1, cfg.maxCharms);
    return cfg.manualSlots.slice(0, n).map(function (s) {
      var top = s.y * aspect;                 // ring y in width-fraction units
      // Charm hangs FROM the ring (top / chainYf); the empty bubble sits just
      // BELOW the ring — where the charm rests — same offset the chain uses, so
      // the guide reads "drop under this hanger" and the charm fills that space.
      return { cx: s.x, top: top, r: d / 2, cy: top + d * 0.46, chainYf: s.y };
    });
  }

  /* ── how a charm sits inside its bubble ──
     Hung by its jump ring's CONTACT EDGE on the chain, body fully below, sized
     by its real silhouette. All values normalized to base-image WIDTH (=1);
     vertical positions run 0..aspect (image height in width units). */
  function charmGeom(box, slot, chain, cfg, targets) {
    var sizes = targets || cfg.sizeTargets;
    var target = (sizes[box.size] || sizes.medium);
    var bw = box.r - box.l, bh = box.b - box.t;
    var imgW = target / Math.max(bw, bh * box.ar);   // longest ink side == target
    var imgH = imgW * box.ar;
    var aspect = chain.aspect;

    var ring = box.ring || { x: (box.l + box.r) / 2, contactY: box.t };
    var ringX = (ring.x != null) ? ring.x : (box.l + box.r) / 2;
    var bails = (ring.bails && ring.bails.length) ? ring.bails
              : [{ x: ringX, contactY: (ring.contactY != null ? ring.contactY : (ring.y != null ? ring.y : box.t)) }];
    var seatBias = (cfg.seat || 0);      // small extra downward seat, width-frac

    // x: hang from the ring x (centroid for two bails)
    var imgLeft = slot.cx - ringX * imgW;

    // y (seat): lower the charm until the TIGHTEST bail just meets its OWN local
    // chain point — so on a two-bail charm neither loop floats above the chain.
    function seatTop(left) {
      var t = -Infinity;
      for (var bi = 0; bi < bails.length; bi++) {
        var absX = left + bails[bi].x * imgW;
        var cyf = (chain && chain.prof) ? chainY(chain, absX) : (slot.chainYf != null ? slot.chainYf : slot.top / aspect);
        var st = cyf * aspect - bails[bi].contactY * imgH;
        if (st > t) t = st;
      }
      return isFinite(t) ? t : (slot.top - bails[0].contactY * imgH);
    }
    var imgTop = seatTop(imgLeft) + seatBias;

    // 4-edge fit: keep the ink box on-image. Horizontal → shift; vertical
    // overflow at the bottom (tall charm) → shrink about the contact, then re-seat.
    function clampX() {
      var iL = imgLeft + box.l * imgW, iR = imgLeft + box.r * imgW;
      if (iL < 0) imgLeft -= iL; else if (iR > 1) imgLeft -= (iR - 1);
    }
    clampX();
    imgTop = seatTop(imgLeft) + seatBias;
    var inkTop = imgTop + box.t * imgH, inkBottom = imgTop + box.b * imgH;
    if (inkBottom > aspect) {
      var avail = aspect - inkTop, need = inkBottom - inkTop;
      if (need > 0 && avail > 0) {
        var sc = Math.max(0.55, Math.min(1, avail / need));
        imgW *= sc; imgH *= sc;
        imgLeft = slot.cx - ringX * imgW; clampX();
        imgTop = seatTop(imgLeft) + seatBias;
      }
    }

    return {
      imgLeft: imgLeft,
      imgTop: imgTop,
      inkLeft: imgLeft + box.l * imgW,
      inkTop: imgTop + box.t * imgH,
      inkW: bw * imgW,
      inkH: bh * imgH,
      imgW: imgW,
      imgH: imgH,
      offL: -(box.l * imgW),
      offT: -(box.t * imgH),
      aspect: aspect,
      contactFrac: bails[0].contactY,   // for placed-drag snap point
      opaque: !!box.opaque
    };
  }

  /* ── component ── */
  function Builder(root) {
    var cfg = JSON.parse(root.querySelector('[data-lecb-config]').textContent);
    try { cfg.fallbackSlots = cfg.fallbackSlots ? JSON.parse(cfg.fallbackSlots) : null; } catch (_) { cfg.fallbackSlots = null; }
    try { cfg.manualSlots = cfg.manualSlots ? JSON.parse(cfg.manualSlots) : null; } catch (_) { cfg.manualSlots = null; }
    var $ = function (s) { return root.querySelector(s); };
    var $$ = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };

    var el = {
      preview: $('[data-lecb-preview]'),
      slots: $('[data-lecb-slots]'),
      empty: $('[data-lecb-empty]'),
      charms: $('[data-lecb-charms]'),
      scroll: $('[data-lecb-scroll]'),
      sizes: $('[data-lecb-sizes]'),
      cats: $('[data-lecb-cats]'),
      search: $('[data-lecb-search]'),
      results: $('[data-lecb-results]'),
      none: $('[data-lecb-none]'),
      summary: $('[data-lecb-summary]'),
      pips: $('[data-lecb-pips]'),
      error: $('[data-lecb-error]'),
      mobar: $('[data-lecb-mobar]')
    };

    // slots[i] = null | charm  — a FIXED array, exactly like the reference
    var state = {
      all: [], view: [], slots: new Array(cfg.maxCharms).fill(null),
      chain: null, geom: [], layout: [], rev: 0, activeSlot: -1,
      size: 'all', cat: 'all', q: '', busy: false
    };
    var suppressClick = false;

    function filled() { return state.slots.filter(Boolean); }

    // Charms use a SEPARATE size set on phones (< 1000px) so large/medium/small/
    // tiny can be tuned independently for mobile vs desktop. Resolved once here so
    // the on-screen necklace and the cart PNG (both read this snapshot) agree.
    function activeTargets() {
      return (window.innerWidth < 1000 && cfg.sizeTargetsMobile) ? cfg.sizeTargetsMobile : cfg.sizeTargets;
    }

    // S1: one geometry snapshot. Rebuilt on ANY slot/box/layout change; both
    // paintSlots() and compose() READ it, neither recomputes — so the necklace
    // and the saved necklace.png can never disagree.
    function buildLayout() {
      var targets = activeTargets();
      state.layout = state.slots.map(function (c, i) {
        if (!c || !c.box || !state.geom[i]) return null;
        return charmGeom(Object.assign({ size: c.size }, c.box), state.geom[i], state.chain, cfg, targets);
      });
      state.rev++;
    }

    function money(n) {
      var s = (Math.round(n * 100) / 100).toFixed(2);
      return (cfg.moneyFormat || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, s);
    }

    function sizeOf(title) {
      var m = /\((small-tiny|small|medium|large|tiny)\)/i.exec(title);
      if (!m) return null;
      var s = m[1].toLowerCase();
      return (s === 'small-tiny' || s === 'tiny') ? 'tiny' : s;
    }
    function bucket(s) { return s; }   // tiny is its own filter now (small-tiny + tiny)

    function prettyCat(t) {
      return t.replace(/^charms?_/i, '').replace(/_/g, ' ')
        .replace(/\b\w/g, function (c) { return c.toUpperCase(); })
        .replace(/\bAnd\b/g, '&');
    }

    function load() {
      var ex = String(cfg.excludeTags || '').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
      return fetch('/collections/' + cfg.collectionHandle + '/products.json?limit=250')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          state.all = (d.products || []).filter(function (p) {
            var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
            if (ex.some(function (t) { return tags.indexOf(t) > -1; })) return false;  // chains + clasps out
            var v = p.variants && p.variants[0];
            return v && v.available && p.images && p.images.length;
          }).map(function (p) {
            var v = p.variants[0];
            var size = sizeOf(p.title);
            return {
              id: v.id,
              title: p.title.replace(/\s*-\s*Waterproof\s*$/i, ''),
              price: parseFloat(v.price),
              img: p.images[0].src,
              size: size || 'medium',
              bucket: size ? bucket(size) : null,
              cats: (p.tags || []).filter(function (t) { return /^charms?_/i.test(t); })
            };
          });
        });
    }

    /* filters */
    function chip(kind, val, label, n) {
      return '<button type="button" class="lecb__chip" data-chip="' + kind + '" data-val="' + val + '">' +
        label + '<span class="lecb__chip-n">' + n + '</span></button>';
    }

    function buildFilters() {
      var counts = { tiny: 0, small: 0, medium: 0, large: 0 }, cats = {};
      state.all.forEach(function (c) {
        if (c.bucket) counts[c.bucket]++;
        c.cats.forEach(function (t) { var k = t.toLowerCase(); cats[k] = (cats[k] || 0) + 1; });
      });
      el.sizes.innerHTML = [['all', 'All', state.all.length], ['tiny', 'Tiny', counts.tiny],
        ['small', 'Small', counts.small], ['medium', 'Medium', counts.medium], ['large', 'Large', counts.large]]
        .map(function (s) { return chip('size', s[0], s[1], s[2]); }).join('');
      el.cats.innerHTML = [chip('cat', 'all', 'All', state.all.length)].concat(
        Object.keys(cats).sort(function (a, b) { return cats[b] - cats[a]; })
          .map(function (t) { return chip('cat', t, prettyCat(t), cats[t]); })).join('');
      syncChips();
    }

    function syncChips() {
      $$('[data-chip]').forEach(function (b) {
        var on = b.getAttribute('data-chip') === 'size'
          ? b.getAttribute('data-val') === state.size
          : b.getAttribute('data-val') === state.cat;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function renderGrid() {
      var q = state.q.trim().toLowerCase();
      state.view = state.all.filter(function (c) {
        if (state.size !== 'all' && c.bucket !== state.size) return false;
        if (state.cat !== 'all' && c.cats.map(function (t) { return t.toLowerCase(); }).indexOf(state.cat) < 0) return false;
        if (q && c.title.toLowerCase().indexOf(q) < 0) return false;
        return true;
      });
      var full = filled().length >= cfg.maxCharms;
      el.charms.innerHTML = state.view.map(function (c) {
        var n = filled().filter(function (p) { return p.id === c.id; }).length;
        var safe = c.title.replace(/"/g, '');
        return '<div class="lecb__cell">' +
          '<button type="button" class="lecb__charm' + (n ? ' is-picked' : '') + '"' +
          ' data-id="' + c.id + '"' +
          ' aria-label="Add ' + safe + ', ' + money(c.price) + '">' +
          '<span class="lecb__charm-img">' +
          '<img src="' + sized(c.img, 200) + '" alt="" loading="lazy" width="200" height="200" draggable="false">' +
          '</span>' +
          '<span class="lecb__charm-name">' + c.title + '</span>' +
          '<span class="lecb__charm-price">' + money(c.price) + '</span>' +
          '<span class="lecb__charm-add" aria-hidden="true">+</span>' +
          '</button>' +
          (n ? badgeHtml(n) + removeHtml(c.id, safe) : '') +
          '</div>';
      }).join('');
      el.none.hidden = state.view.length > 0;
      el.results.textContent = state.view.length + (state.view.length === 1 ? ' charm' : ' charms');
      el.charms.classList.toggle('is-full', full);
    }

    function badgeHtml(n) {
      return '<span class="lecb__charm-badge" aria-hidden="true">' + n + '</span>';
    }
    function removeHtml(id, safe) {
      return '<button type="button" class="lecb__charm-x" data-unpick="' + id + '" aria-label="Remove ' + safe + ' from your necklace">' +
        '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>' +
        '</button>';
    }

    /* ── the bubbles ── */
    function buildSlots() {
      state.geom = (cfg.manualSlots && cfg.manualSlots.length) ? manualBubbles(state.chain, cfg)
        : (state.chain && state.chain.fromCoords) ? state.chain.fromCoords
        : (cfg.layout === 'clustered' ? clusterBubbles(state.chain, cfg) : bubbles(state.chain, cfg));
      // Bubbles are NON-INTERACTIVE visual guides now — the preview-level pointer
      // layer (previewPointer) owns all slot actions geometrically, so closely
      // spaced slots never fight over overlapping hit boxes. Only the remove-X
      // stays a real focusable control (keyboard/AT removal path).
      el.slots.innerHTML = state.geom.map(function (s, i) {
        return '<div class="lecb__slot" data-slot="' + i + '" ' +
          'style="left:' + (s.cx * 100) + '%; top:' + ((s.cy / state.chain.aspect) * 100) + '%;' +
          'width:' + (s.r * 2 * 100) + '%;">' +
          '<span class="lecb__bubble" aria-hidden="true">' +
          '<span class="lecb__bubble-plus">+</span>' +
          '</span>' +
          '<span class="lecb__charm-slot" data-charm-slot="' + i + '"></span>' +
          '<button type="button" class="lecb__slot-x" data-remove="' + i + '" aria-label="Remove charm from slot ' + (i + 1) + '">' +
          '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" fill="none"/></svg>' +
          '</button>' +
          '</div>';
      }).join('');
    }

    function paintSlots() {
      buildLayout();
      state.geom.forEach(function (s, i) {
        var c = state.slots[i];
        var wrap = el.slots.querySelector('[data-slot="' + i + '"]');
        var host = wrap.querySelector('[data-charm-slot="' + i + '"]');

        var ready = !!(c && c.box && state.layout[i]);
        wrap.classList.toggle('is-filled', ready);
        wrap.classList.toggle('is-loading', !!c && !c.box);
        wrap.classList.toggle('is-active', state.activeSlot === i && !c);

        if (!ready) { host.innerHTML = ''; host.removeAttribute('data-charm'); return; }

        var g = state.layout[i];

        // Replay the landing swing every time a DIFFERENT charm arrives here.
        // The host is built once, with the bubbles — so a CSS animation declared
        // on it only ever fired at page load, while the slot was still empty.
        // Nobody has actually seen the swing until now.
        var isNew = host.getAttribute('data-charm') !== String(c.id);
        var img = host.querySelector('img');

        if (isNew || !img) {
          host.innerHTML = '<img alt="" draggable="false">';
          img = host.querySelector('img');
          host.setAttribute('data-charm', c.id);
          host.classList.remove('is-landing');
          void host.offsetWidth;                     // reflow, so the animation restarts
          host.classList.add('is-landing');
          host.addEventListener('animationend', function onEnd(ev) {
            if (ev.target !== host) return;          // not the image's own animation
            host.classList.remove('is-landing');     // cleared, so hover can sway it after
            host.removeEventListener('animationend', onEnd);
          });
        }

        if (img.getAttribute('src') !== sized(c.img, 400)) img.src = sized(c.img, 400);
        host.classList.toggle('is-photo', !!c.box.opaque);

        // The charm's box is its INK, positioned relative to the SLOT wrapper
        // (which is the bubble). Percentages here are of the bubble's own size.
        var bw = s.r * 2;                                   // bubble width, fraction of base
        host.style.left = ((g.inkLeft - (s.cx - s.r)) / bw * 100) + '%';
        host.style.top = ((g.inkTop - (s.cy - s.r)) / bw * 100) + '%';
        host.style.width = (g.inkW / bw * 100) + '%';
        host.style.height = (g.inkH / bw * 100) + '%';

        img.style.width = (g.imgW / g.inkW * 100) + '%';
        img.style.left = (g.offL / g.inkW * 100) + '%';
        img.style.top = (g.offT / g.inkH * 100) + '%';
      });
    }

    function paint() {
      var n = filled().length;
      el.empty.hidden = n > 0;

      var pips = '';
      for (var i = 0; i < cfg.maxCharms; i++) pips += '<i class="' + (state.slots[i] ? 'on' : '') + '"></i>';
      el.pips.innerHTML = pips;
      $$('[data-lecb-count],[data-lecb-count-mo]').forEach(function (e) { e.textContent = n; });

      var total = cfg.basePrice + filled().reduce(function (s, c) { return s + c.price; }, 0);
      $$('[data-lecb-total],[data-lecb-total-mo]').forEach(function (e) { e.textContent = money(total); });

      el.summary.innerHTML =
        '<li class="lecb__row lecb__row--base">' +
        '<img src="' + sized(cfg.baseImage, 100) + '" alt="" width="40" height="40">' +
        '<span class="lecb__row-name">' + cfg.baseTitle + '</span>' +
        '<span class="lecb__row-price">' + money(cfg.basePrice) + '</span></li>' +
        state.slots.map(function (c, i) {
          if (!c) return '';
          return '<li class="lecb__row">' +
            '<img src="' + sized(c.img, 100) + '" alt="" width="40" height="40">' +
            '<span class="lecb__row-name">' + c.title + '</span>' +
            '<span class="lecb__row-price">' + money(c.price) + '</span>' +
            '<button type="button" class="lecb__row-x" data-remove="' + i + '" aria-label="Remove ' + c.title.replace(/"/g, '') + '">' +
            '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg>' +
            '</button></li>';
        }).join('');

      if (state.chain) paintSlots();
      el.mobar.classList.toggle('is-on', n > 0);
      syncCards();                // NOT renderGrid() — rebuilding 185 cards on
    }                             // every drop is what made the drop stutter

    // Update only what changed on the cards: the picked highlight and its count.
    function syncCards() {
      var counts = {};
      filled().forEach(function (c) { counts[c.id] = (counts[c.id] || 0) + 1; });
      el.charms.classList.toggle('is-full', filled().length >= cfg.maxCharms);
      $$('.lecb__cell').forEach(function (cell) {
        var card = cell.querySelector('.lecb__charm');
        var id = Number(card.getAttribute('data-id'));
        var n = counts[id] || 0;
        card.classList.toggle('is-picked', n > 0);

        var badge = cell.querySelector('.lecb__charm-badge');
        var x = cell.querySelector('.lecb__charm-x');
        if (n > 0) {
          if (!badge) cell.insertAdjacentHTML('beforeend', badgeHtml(n));
          else badge.textContent = n;
          if (!x) cell.insertAdjacentHTML('beforeend', removeHtml(id, card.getAttribute('aria-label')));
        } else {
          if (badge) badge.remove();
          if (x) x.remove();
        }
      });
    }

    /* ── add / replace / remove ── */
    var claim = [];                        // per-slot token, so a slower earlier
                                           // drop can't overwrite a later one

    function place(id, slotIndex) {
      var c = state.all.filter(function (x) { return x.id === id; })[0];
      if (!c) return;

      var i;
      if (typeof slotIndex === 'number') {
        i = slotIndex;
      } else if (state.activeSlot >= 0 && !state.slots[state.activeSlot]) {
        i = state.activeSlot;                              // a bubble was tapped to target it
        state.activeSlot = -1;                             // consume synchronously
      } else {
        i = state.slots.indexOf(null);                     // else next free
      }
      if (i < 0) { flash(cfg.strings.full); return; }     // all bubbles taken
      if (state.activeSlot === i) state.activeSlot = -1;

      // Claim the bubble SYNCHRONOUSLY. The artwork loads async, and if we waited
      // for it before reserving, five quick clicks would all read "next free
      // bubble = 0" and stamp over each other — only one charm would land.
      var token = (claim[i] = (claim[i] || 0) + 1);
      state.slots[i] = Object.assign({}, c, { box: null });   // pending
      paint();                                               // count/total update now

      // Then wait for the FULL-SIZE artwork before mounting it, so the landing
      // animation never plays against an image that hasn't decoded.
      Promise.all([
        measureCharm(c.img),
        loadImage(sized(c.img, 400)).catch(function () { return null; })
      ]).then(function (r) {
        if (claim[i] !== token) return;                      // superseded by a later drop
        state.slots[i] = Object.assign({}, c, { box: r[0] });
        paint();
      });
    }

    function remove(i) {
      claim[i] = (claim[i] || 0) + 1;      // cancel any in-flight load for this bubble
      state.slots[i] = null;
      paint();
    }

    function flash(msg) {
      el.error.textContent = msg;
      el.error.hidden = false;
      clearTimeout(flash._t);
      flash._t = setTimeout(function () { el.error.hidden = true; }, 2600);
    }

    /* ── drag: ONE pointer-owned state machine (S2). ───────────────────────
       A single `drag` session at a time, keyed by pointerId. It serves three
       gestures: dragging a card from the picker, dragging a PLACED charm to
       another bubble (reposition/swap), and tapping an empty bubble to target
       it. Every window handler ignores events whose pointerId isn't the owner,
       so a stray second pointer, a stale ghost, or a leftover timer can never
       commit the wrong action. */
    var drag = null;
    var ghost = null;
    var HOLD = 200, THRESH = 6;

    function charmById(id) { return state.all.filter(function (x) { return x.id === id; })[0]; }

    function armSuppressClick() {                 // gesture-scoped, auto-expiring
      suppressClick = true;
      clearTimeout(armSuppressClick._t);
      armSuppressClick._t = setTimeout(function () { suppressClick = false; }, 350);
    }

    function dragging() {
      function makeGhost(src) {
        if (ghost) return;
        ghost = document.createElement('img');
        ghost.className = 'lecb__ghost';
        ghost.src = src;
        document.body.appendChild(ghost);
        el.preview.classList.add('is-dropping');
      }

      function endDrag() {
        if (drag) {
          if (drag.holdTimer) clearTimeout(drag.holdTimer);
          if (drag.liftCard) drag.liftCard.classList.remove('is-lifting');
        }
        el.preview.classList.remove('is-dropping');
        highlight(-1);
        if (ghost) { ghost.remove(); ghost = null; }
        drag = null;
      }

      // ── PICKER: start dragging a library card ──
      el.charms.addEventListener('pointerdown', function (e) {
        if (drag || e.button > 0) return;
        var b = e.target.closest('.lecb__charm');
        if (!b) return;
        var d = { pointerId: e.pointerId, source: 'picker', id: Number(b.getAttribute('data-id')),
                  touch: e.pointerType === 'touch', sx: e.clientX, sy: e.clientY,
                  moved: false, armed: false, holdTimer: null, liftCard: null };
        drag = d;
        if (!d.touch) return;                     // mouse: arms on the first real move
        if (e.target.closest('.lecb__charm-add')) {          // the "+" grip lifts instantly
          d.armed = true;
          try { el.charms.setPointerCapture(e.pointerId); } catch (_) {}
        } else {                                  // card body: short hold to lift, else it scrolls
          d.holdTimer = setTimeout(function () {
            if (drag !== d) return;
            d.armed = true; d.liftCard = b; b.classList.add('is-lifting');
            if (navigator.vibrate) { try { navigator.vibrate(12); } catch (_) {} }
            try { el.charms.setPointerCapture(e.pointerId); } catch (_) {}
          }, HOLD);
        }
      });

      // ── PREVIEW: geometric nearest-slot hit testing owns all slot actions.
      // Bubbles are non-interactive; the real controls (remove X) act on click. ──
      el.preview.addEventListener('pointerdown', function (e) {
        if (drag || e.button > 0) return;
        if (e.target.closest('[data-remove],[data-unpick],a,button')) return;
        var i = nearestSlot(e.clientX, e.clientY);      // real-radius hit required
        if (i < 0) return;                              // tap on bare necklace = nothing
        var c = state.slots[i];
        if (c && c.box) {                               // grab a placed charm to reposition
          var lay = state.layout[i];
          drag = { pointerId: e.pointerId, source: 'slot', id: c.id, fromSlot: i,
                   sourceRef: c, sourceClaim: claim[i] || 0,
                   contactFrac: (lay && lay.contactFrac != null) ? lay.contactFrac : 0.16,
                   ghostSrc: sized(c.img, 200), touch: e.pointerType === 'touch',
                   sx: e.clientX, sy: e.clientY, moved: false, armed: false, holdTimer: null };
          if (drag.touch) {                             // lift on hold; quick tap removes
            var dd = drag;
            dd.holdTimer = setTimeout(function () {
              if (drag !== dd) return;
              dd.armed = true; el.preview.classList.add('is-dropping');
              if (navigator.vibrate) { try { navigator.vibrate(12); } catch (_) {} }
            }, HOLD);
          }
          try { el.preview.setPointerCapture(e.pointerId); } catch (_) {}
        } else {                                        // empty slot: candidate activation
          drag = { pointerId: e.pointerId, source: 'empty', slot: i,
                   touch: e.pointerType === 'touch', sx: e.clientX, sy: e.clientY, moved: false, armed: false };
          try { el.preview.setPointerCapture(e.pointerId); } catch (_) {}
        }
      });

      // Non-passive: stop the page scrolling under the finger only while a touch
      // drag is actually armed AND moving.
      window.addEventListener('touchmove', function (e) {
        if (drag && drag.armed && drag.moved) e.preventDefault();
      }, { passive: false });

      window.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== drag.pointerId) return;
        var dx = Math.abs(e.clientX - drag.sx), dy = Math.abs(e.clientY - drag.sy);

        if (drag.source === 'empty') {                  // scroll intent cancels activation
          if (dx > 10 || dy > 10) endDrag();
          return;
        }

        if (!drag.armed) {
          if (drag.touch) {                             // hold pending — early move = scroll
            if (dx > 10 || dy > 10) { clearTimeout(drag.holdTimer); endDrag(); }
            return;
          }
          if (dx < THRESH && dy < THRESH) return;       // mouse arms on threshold
          drag.armed = true;
        }

        if (!drag.moved) {
          if (dx < THRESH && dy < THRESH) return;
          drag.moved = true;
          var src = drag.source === 'slot' ? drag.ghostSrc : (charmById(drag.id) ? sized(charmById(drag.id).img, 200) : null);
          if (src) makeGhost(src);
        }
        if (ghost) {
          var gw = ghost.offsetWidth || 78, gh = ghost.offsetHeight || 78;
          var lift = e.pointerType === 'touch' ? gh * 0.82 : gh * 0.5;
          ghost.style.left = (e.clientX - gw / 2) + 'px';
          ghost.style.top = (e.clientY - lift) + 'px';
        }
        var rp = ringPoint(e);
        highlight(nearestSlot(rp.x, rp.y));             // snap by the charm's ring, not the finger
        e.preventDefault();
      }, { passive: false });

      window.addEventListener('pointerup', function (e) {
        if (!drag || e.pointerId !== drag.pointerId) return;
        var d = drag;

        if (d.source === 'empty') {
          var j = nearestSlot(e.clientX, e.clientY);
          if (!d.armed && j === d.slot && !state.slots[j]) {        // unarmed release, same slot
            state.activeSlot = (state.activeSlot === j) ? -1 : j;
            paint();
            if (window.innerWidth < 1000 && state.activeSlot === j) scrollToLibrary();
          }
          endDrag();
          return;
        }

        if (d.moved && d.armed) {                        // a real drag → drop
          armSuppressClick();
          if (d.source === 'slot') commitReposition(d, e);
          else {
            var rp = ringPoint(e), j2 = nearestSlot(rp.x, rp.y);
            if (j2 > -1) place(d.id, j2);
          }
          endDrag();
          return;
        }

        // no move:
        if (d.source === 'slot' && !d.armed) remove(d.fromSlot);   // quick tap = remove
        // picker tap (no move) → let the root click handler place() it
        endDrag();
      });

      window.addEventListener('pointercancel', function (e) {
        if (!drag || e.pointerId !== drag.pointerId) return;
        endDrag();
      });
    }

    // Move or swap a placed charm. Guards (per Codex): the source must be
    // unchanged since pickup, neither end may be mid-load, and both claim
    // tokens bump atomically so an in-flight measure can't resurrect a charm.
    function commitReposition(d, e) {
      var rp = ringPoint(e);
      var j = nearestSlot(rp.x, rp.y);
      if (j < 0 || j === d.fromSlot) { paint(); return; }                 // snap back
      if (state.slots[d.fromSlot] !== d.sourceRef || (claim[d.fromSlot] || 0) !== d.sourceClaim) { paint(); return; }
      var tgt = state.slots[j];
      if (!d.sourceRef.box || (tgt && !tgt.box)) { paint(); return; }     // don't move a loading charm
      claim[d.fromSlot] = (claim[d.fromSlot] || 0) + 1;
      claim[j] = (claim[j] || 0) + 1;
      if (tgt) { state.slots[d.fromSlot] = tgt; state.slots[j] = d.sourceRef; }   // swap
      else { state.slots[j] = d.sourceRef; state.slots[d.fromSlot] = null; }      // move
      if (state.activeSlot === j || state.activeSlot === d.fromSlot) state.activeSlot = -1;
      paint();
    }

    function scrollToLibrary() {
      var lib = root.querySelector('.lecb__library');
      if (lib && lib.scrollIntoView) lib.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Snap by the charm's CLASP (jump ring), not the fingertip. For a placed
    // charm we know its measured contact fraction; for a picker card we don't
    // (not measured yet), so use the ~16% heuristic.
    function ringPoint(e) {
      var gh = ghost ? ghost.offsetHeight : 78;
      var lift = e.pointerType === 'touch' ? gh * 0.82 : gh * 0.5;
      var frac = (drag && drag.source === 'slot' && drag.contactFrac != null) ? drag.contactFrac : 0.16;
      return { x: e.clientX, y: e.clientY - lift + gh * frac };
    }

    // Nearest bubble to (x,y) — but ONLY if genuinely near one (real-radius
    // hit) and inside the preview. Returns -1 otherwise, so a tap/drop on bare
    // necklace space does nothing.
    function nearestSlot(x, y) {
      var box = el.preview.getBoundingClientRect();
      if (x < box.left || x > box.right || y < box.top || y > box.bottom) return -1;
      var best = -1, bestD = Infinity;
      $$('.lecb__slot').forEach(function (s, i) {
        var r = s.getBoundingClientRect();
        var dx = x - (r.left + r.width / 2), dy = y - (r.top + r.height / 2);
        var d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
      });
      var radius = box.width * 0.18;
      return (Math.sqrt(bestD) <= radius) ? best : -1;
    }

    function highlight(i) {
      $$('.lecb__slot').forEach(function (s, k) { s.classList.toggle('is-target', k === i); });
    }

    /* ── cart ── */
    function compose() {
      // Read the SAME layout snapshot the necklace is rendered from (S1), so the
      // saved image can never disagree with what she designed. Best-effort: any
      // failure resolves to null and never blocks add-to-cart.
      buildLayout();
      var snap = state.layout.slice();
      var slots = state.slots.slice();
      return loadImage(cfg.baseImage).then(function (base) {
        var W = COMPOSE, H = Math.round(W * (base.naturalHeight / base.naturalWidth));
        var c = document.createElement('canvas');
        c.width = W; c.height = H;
        var x = c.getContext('2d');
        x.drawImage(base, 0, 0, W, H);

        var jobs = [];
        slots.forEach(function (p, i) {
          if (!p || !p.box || !snap[i]) return;
          jobs.push(loadImage(sized(p.img, 600)).then(function (im) {
            return { im: im, g: snap[i], opaque: p.box.opaque };
          }).catch(function () { return null; }));
        });

        return Promise.all(jobs).then(function (list) {
          list.filter(Boolean).forEach(function (o) {
            var w = o.g.imgW * W;
            var h = w * (o.im.naturalHeight / o.im.naturalWidth);
            var dx = o.g.imgLeft * W;
            var dy = (o.g.imgTop / o.g.aspect) * H;
            if (o.opaque) {
              var ink = o.g.inkW * W;
              x.save();
              x.beginPath();
              x.arc(o.g.inkLeft * W + ink / 2, (o.g.inkTop / o.g.aspect) * H + ink / 2, ink / 2, 0, Math.PI * 2);
              x.closePath();
              x.clip();
              x.drawImage(o.im, dx, dy, w, h);
              x.restore();
            } else {
              x.drawImage(o.im, dx, dy, w, h);
            }
          });
          return new Promise(function (res) {
            c.toBlob(function (b) { res(b ? new File([b], 'necklace.png', { type: 'image/png' }) : null); }, 'image/png');
          });
        });
      }).catch(function () { return null; });
    }

    function addToCart() {
      if (state.busy || !cfg.baseAvailable) return;
      state.busy = true;
      var btns = $$('[data-lecb-atc]');
      btns.forEach(function (b) { b.disabled = true; b.classList.add('is-busy'); });

      compose().then(function (file) {
        var fd = new FormData();
        var bundle = (self.crypto && crypto.randomUUID) ? crypto.randomUUID()
          : String(Date.now()) + Math.random().toString(36).slice(2);

        var lines = [{ id: cfg.baseVariantId, qty: 1 }];
        var seen = {};
        filled().forEach(function (c) { seen[c.id] = (seen[c.id] || 0) + 1; });
        Object.keys(seen).forEach(function (id) { lines.push({ id: Number(id), qty: seen[id] }); });

        lines.forEach(function (l, i) {
          fd.append('items[' + i + '][id]', l.id);
          fd.append('items[' + i + '][quantity]', l.qty);
          fd.append('items[' + i + '][properties][_bundle_id]', bundle);
          fd.append('items[' + i + '][properties][_bundle_role]', i === 0 ? 'base' : 'charm');
          if (i === 0) {
            fd.append('items[' + i + '][properties][_charm_count]', String(filled().length));
            // ordered slotIndex:variantId map — preserves position + duplicates
            // even when the PNG fails to compose.
            var layoutStr = state.slots.map(function (cc, k) { return cc ? (k + ':' + cc.id) : null; })
              .filter(Boolean).join(',');
            if (layoutStr) fd.append('items[' + i + '][properties][_layout]', layoutStr);
            if (file) fd.append('items[' + i + '][properties][_design]', file);
          }
        });

        return fetch('/cart/add.js', { method: 'POST', body: fd }).then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || 'Could not add to cart'); });
          return r.json();
        });
      }).then(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return fetch('/cart.js').then(function (r) { return r.json(); });
      }).then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (e) { e.textContent = cart.item_count; });
        var open = document.querySelector('.js-drawer-open-cart, [data-action="open-cart"], .site-header__cart');
        if (open) open.click(); else window.location.href = '/cart';
      }).catch(function (err) {
        flash(err.message || 'Could not add to cart. Please try again.');
      }).then(function () {
        state.busy = false;
        btns.forEach(function (b) { b.disabled = false; b.classList.remove('is-busy'); });
      });
    }

    /* ── events ── */
    root.addEventListener('click', function (e) {
      if (suppressClick) { suppressClick = false; e.preventDefault(); e.stopPropagation(); return; }

      var chip = e.target.closest('[data-chip]');
      if (chip) {
        if (chip.getAttribute('data-chip') === 'size') state.size = chip.getAttribute('data-val');
        else state.cat = chip.getAttribute('data-val');
        syncChips();
        renderGrid();
        syncCards();                       // keep the picked highlights after a re-render
        el.scroll.scrollTop = 0;
        return;
      }

      var un = e.target.closest('[data-unpick]');
      if (un) {                                    // remove from the PICKER, not the preview
        var uid = Number(un.getAttribute('data-unpick'));
        for (var k = state.slots.length - 1; k >= 0; k--) {
          if (state.slots[k] && state.slots[k].id === uid) { remove(k); break; }
        }
        return;
      }

      var rm = e.target.closest('[data-remove]');
      if (rm) { remove(Number(rm.getAttribute('data-remove'))); return; }

      var tile = e.target.closest('.lecb__charm');
      if (tile) { place(Number(tile.getAttribute('data-id'))); return; }   // → active slot, else next free

      if (e.target.closest('[data-lecb-atc]')) addToCart();
    });

    el.search.addEventListener('input', function () { state.q = el.search.value; renderGrid(); syncCards(); });
    // On mobile the necklace is pinned at the top and the filter bar pins right
    // beneath it. CSS can't know the necklace's rendered height, so measure it
    // and hand it over as a custom property.
    function syncStageHeight() {
      var stage = root.querySelector('.lecb__stage');
      root.style.setProperty('--lecb-stage-h', (stage ? stage.offsetHeight : 0) + 'px');
    }

    var lecbWasMobile = window.innerWidth < 1000;
    window.addEventListener('resize', function () {
      var nowMobile = window.innerWidth < 1000;
      if (nowMobile !== lecbWasMobile) { lecbWasMobile = nowMobile; if (state.chain) buildLayout(); }  // switch mobile/desktop charm sizes
      if (state.chain) paintSlots();
      syncStageHeight();
    });
    syncStageHeight();
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(syncStageHeight);
      ro.observe(root.querySelector('.lecb__stage'));
    }

    function showLoadError() {
      el.charms.innerHTML = '';
      el.none.hidden = false;
      el.none.textContent = 'We couldn’t load the charms. Please turn off any content blocker for this page, or try a different browser.';
      root.classList.add('is-ready', 'is-error');
    }

    function fallbackChainForBuild() {
      if (cfg.fallbackSlots && cfg.fallbackSlots.length) {
        return { fromCoords: cfg.fallbackSlots, aspect: cfg.baseAspect ? (1 / cfg.baseAspect) : 1, valid: false };
      }
      return fallbackChain(cfg);
    }

    dragging();                    // wire pointer listeners once (harmless before slots exist)

    // The charm library renders as soon as the CATALOG loads — INDEPENDENT of the
    // canvas trace. A blocked/failed trace (Firefox ETP-strict etc.) can no longer
    // blank the charms; at worst the bubble geometry falls back.
    load().then(function () {
      buildFilters();
      renderGrid();
      paint();                     // sync card state (chain may not be ready yet)
      root.classList.add('is-ready');
    }).catch(function (err) {
      console.warn('[lecb] catalog load failed:', err && err.message);
      showLoadError();
    });

    // Trace the chain on its own boundary; fall back to stored coords / synthetic U.
    traceChain(cfg.baseImage).then(function (chain) {
      state.chain = chain;
    }).catch(function (err) {
      console.warn('[lecb] chain trace fell back:', err && err.message);
      state.chain = fallbackChainForBuild();
    }).then(function () {
      // Manual (fixed-ring) products: drop the traced profile so charms seat on
      // their OWN slot y (the ring), not the traced bar. Keep only the aspect.
      if (cfg.manualSlots && cfg.manualSlots.length) {
        var asp = (state.chain && state.chain.aspect) ? state.chain.aspect : (cfg.baseAspect ? (1 / cfg.baseAspect) : 1);
        state.chain = { aspect: asp, valid: false, manual: true };
      }
      buildSlots();
      paint();
    });
  }

  function init() {
    document.querySelectorAll('[data-lecb]').forEach(function (n) {
      if (!n.__lecb) { n.__lecb = true; new Builder(n); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
